import React, { useState, useEffect, useMemo } from 'react'
import { Box, Stack, Heading, Text, Button, Input, FormControl, Alert, SearchableSelect, Checkbox, ReadOnlyCopyInput, Link } from '@/components/lib'
import api from '../../api'
import utils from '../../utils/utils'
import useStore from '../../store'

export default function ProfileSettings () {
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newTimezone, setNewTimezone] = useState('')
  const { user, team, updateUser } = useStore()

  const firstName = user?.firstName
  const lastName = user?.lastName
  const email = user?.email
  const timezone = user?.timezone

  const timezoneOptions = useMemo(() =>
    Intl.supportedValuesOf('timeZone').map(tz => ({ value: tz, label: tz }))
  , [])

  useEffect(() => {
    setNewFirstName(firstName)
    setNewLastName(lastName)
    setNewEmail(email)
    setNewTimezone(timezone || 'Europe/London')
  }, [firstName, lastName, email, timezone])

  const saveProfile = () => {
    api.users.update(user?._id, { firstName: newFirstName, lastName: newLastName, timezone: newTimezone }, u => {
      updateUser(u._id, u)
      utils.toast('success', 'Profile updated successfully')
    }, err => {
      utils.toast('error', 'Unable to update your profile', err.message)
    })
  }
  const toggleMcp = enabled => {
    // Unlike the fields above, this control renders straight from the store
    // rather than local state, so flip it optimistically and roll back if the
    // request fails — otherwise the checkbox lags a round trip behind the click.
    updateUser(user?._id, { mcpEnabled: enabled })
    api.users.update(user?._id, { mcpEnabled: enabled }, u => {
      updateUser(u._id, u)
      utils.toast('success', enabled ? 'External AI connectors enabled' : 'External AI connectors disabled')
    }, err => {
      updateUser(user?._id, { mcpEnabled: !enabled })
      utils.toast('error', 'Unable to update your settings', err.message)
    })
  }
  const saveEmail = () => {
    api.users.update(user?._id, { email: newEmail }, u => {
      updateUser(u._id, u)
      utils.toast('success', 'Email updated successfully')
    }, err => {
      utils.toast('error', 'Unable to update your email address', err.message)
    })
  }

  return (
    <Stack direction='column' spacing={5}>
      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>About you</Heading>
          <Text>How others see you on Trialflare.</Text>
        </Box>
        <FormControl label='First name'>
          <Input id='firstName' value={newFirstName} onChange={e => setNewFirstName(e.target.value)} placeholder='Jane' />
        </FormControl>
        <FormControl label='Last name'>
          <Input id='lastName' value={newLastName} onChange={e => setNewLastName(e.target.value)} placeholder='Smith' />
        </FormControl>
        <FormControl label='Timezone' helperText='Used when sending you email and notification reminders.'>
          <SearchableSelect
            size='sm'
            options={timezoneOptions}
            value={newTimezone}
            onChange={v => setNewTimezone(v)}
          />
        </FormControl>
        <Box><Button colorPalette='primary' onClick={saveProfile}>Save</Button></Box>
      </Stack>

      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>Your email address</Heading>
          <Text>This is the address you use to login to Trialflare and where you receive notifications.</Text>
        </Box>
        <FormControl label='Email address'>
          <Input id='email' value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder='jane@example.com' />
        </FormControl>
        <Alert>
          For security we will send an email to both your old and new addresses confirming this change.
        </Alert>
        {!user?.hasPassword
          ? (
            <Alert status='warning'>
              Your email address is managed by your organisation. You can't change it here.
            </Alert>)
          : (
            <Box><Button colorPalette='primary' onClick={saveEmail}>Save</Button></Box>)}
      </Stack>

      {utils.hasFeature(team, 'mcpAccess') && (
        <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
          <Box>
            <Heading as='h3' size='md'>External AI connectors (Trialflare MCP server)</Heading>
            <Text>
              Let an external AI assistant such as Claude or ChatGPT work with your trials on
              your behalf.
            </Text>
          </Box>
          <Alert status='warning'>
            When used, data your account can access, which may include participant records and responses,
            may be sent to the AI provider you connect.
          </Alert>
          <Checkbox
            checked={!!user?.mcpEnabled}
            onCheckedChange={e => toggleMcp(e.checked)}
          >
            Allow external AI connectors to access my Trialflare data
          </Checkbox>

          {user?.mcpEnabled && (
            <>
              <FormControl
                label='Connection address'
                helperText='Paste this into your assistant when adding a custom connector, and leave any OAuth client ID and secret fields blank.'
              >
                <ReadOnlyCopyInput value={`${import.meta.env.VITE_API_URL}/mcp`} />
              </FormControl>
              <Text fontSize='sm'>
                <Link
                  href={`${import.meta.env.VITE_HELP_CENTRE_HOST}/ai-connectors/`}
                  target='_blank'
                  rel='noreferrer'
                >
                  How to connect Claude, ChatGPT and others
                </Link>
              </Text>
            </>
          )}
        </Stack>
      )}
    </Stack>
  )
}
